/* ManTire Atelier — storefront behaviour */
(() => {
  const theme = window.theme || {};
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const formatMoney = (cents) => {
    let s = (theme.moneyFormat || 'Rs. {{amount}}').replace(/{{\s*amount\w*\s*}}/, (Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    if (theme.hideZeros) s = s.replace(/\.00(?!\d)/, '');
    if (theme.rupee) s = s.replace(/Rs\.?\s?/, '₹');
    return s;
  };

  const toast = (msg) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 2600);
  };

  /* ---------- scroll lock + focus trap ---------- */
  let lockCount = 0;
  const lockScroll = () => { if (lockCount++ === 0) { document.documentElement.style.overflow = 'hidden'; } };
  const unlockScroll = () => { if (--lockCount <= 0) { lockCount = 0; document.documentElement.style.overflow = ''; } };
  const focusable = (root) => $$('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])', root).filter((el) => el.offsetParent !== null);
  const trapFocus = (root, e) => {
    if (e.key !== 'Tab') return;
    const f = focusable(root); if (!f.length) return;
    const first = f[0]; const last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  /* ---------- drawers & modals ---------- */
  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); trapFocus(this, e); });
    }
    stampLazy(root) {
      root.querySelectorAll('template[data-lazy]').forEach((tpl) => { tpl.replaceWith(tpl.content.cloneNode(true)); });
      if (typeof syncMode === 'function') syncMode();
    }
    open(opener) {
      if (this.classList.contains('is-open')) return;
      this.stampLazy(this);
      this._opener = opener || document.activeElement;
      $$('drawer-panel.is-open, bag-drawer.is-open').forEach((d) => d !== this && d.close());
      this.classList.add('is-open', 'was-open');
      lockScroll();
      setTimeout(() => {
        const input = this.querySelector('[data-search-input]');
        const panel = this.querySelector('.drawer__panel, .modal__panel');
        if (input) input.focus({ preventScroll: true });
        else if (panel) { panel.setAttribute('tabindex', '-1'); panel.focus({ preventScroll: true }); }
      }, 80);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      unlockScroll();
      if (this._opener && this._opener.focus) this._opener.focus({ preventScroll: true });
    }
  }
  customElements.define('drawer-panel', DrawerPanel);

  class ModalDialog extends DrawerPanel {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-modal-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); trapFocus(this, e); });
    }
  }
  customElements.define('modal-dialog', ModalDialog);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); const d = document.getElementById(opener.dataset.drawerOpen); d && d.open(opener); return; }
    const finder = e.target.closest('[data-finder-open]');
    if (finder) { e.preventDefault(); const m = document.getElementById('FinderModal'); m && m.open(finder); return; }
    const cartOpen = e.target.closest('[data-cart-open]');
    if (cartOpen && theme.cartType === 'drawer') { const c = $('bag-drawer'); if (c) { e.preventDefault(); c.open(cartOpen); } }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === '/' && !/input|textarea|select/i.test(document.activeElement.tagName)) {
      const s = document.getElementById('SearchDrawer');
      if (s) { e.preventDefault(); s.open(); }
    }
  });

  /* ---------- sticky header & mega menu ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('[data-header-overlay]');
      let ticking = false;
      let lastY = window.scrollY;
      const compact = window.matchMedia('(max-width: 1099px)');
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const y = window.scrollY;
          this.classList.toggle('is-stuck', this.getBoundingClientRect().top < -2 && y > 40);
          // phones: tuck the header away while reading down, bring it back on the way up
          const host = this.closest('.section-header') || this;
          if (compact.matches && !document.querySelector('drawer-panel.is-open, bag-drawer.is-open, modal-dialog.is-open')) {
            if (y > 240 && y > lastY + 8) host.classList.add('is-tucked');
            else if (y < lastY - 8 || y < 120) host.classList.remove('is-tucked');
          } else host.classList.remove('is-tucked');
          if (Math.abs(y - lastY) > 8) lastY = y;
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      this.items = $$('[data-mega-item]', this);
      this.current = null;
      const nav = this.querySelector('[data-mega-menu]');
      // Mega images sit in hidden panels, so lazy loading would only start on first hover.
      // Warm them after the page settles, or as soon as the pointer heads for the menu.
      const warm = () => {
        if (this._warmed) return;
        this._warmed = true;
        $$('.mega img', this).forEach((img) => { img.loading = 'eager'; img.decoding = 'async'; });
      };
      if (nav) nav.addEventListener('pointerenter', warm, { once: true });
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
      // Only where the desktop menu is on screen; phones never show it, so they never download its images.
      const warmIfShown = () => { if (nav && nav.offsetParent !== null) warm(); };
      if (document.readyState === 'complete') idle(warmIfShown); else window.addEventListener('load', () => idle(warmIfShown), { once: true });

      this.items.forEach((li) => {
        const link = li.querySelector('.nav-menu__link');
        li.addEventListener('pointerenter', (e) => {
          if (e.pointerType === 'touch') return;
          clearTimeout(this._close); clearTimeout(this._open);
          if (this.current) this.openItem(li); // already browsing: switch instantly
          else this._open = setTimeout(() => this.openItem(li), 70);
        });
        li.addEventListener('pointerleave', (e) => {
          if (e.pointerType === 'touch') return;
          clearTimeout(this._open);
          this._close = setTimeout(() => this.closeAll(), 260);
        });
        // touch screens wide enough for the desktop nav: first tap opens, second tap follows the link
        link.addEventListener('pointerdown', (e) => { this._touch = e.pointerType === 'touch'; });
        link.addEventListener('click', (e) => {
          if (this._touch && this.current !== li) { e.preventDefault(); this.openItem(li); }
        });
        link.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown' || ((e.key === 'Enter' || e.key === ' ') && this.current !== li)) {
            e.preventDefault(); this.openItem(li); const f = li.querySelector('.mega a'); f && f.focus();
          }
        });
        li.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.closeAll(); link.focus(); } });
        li.addEventListener('focusout', (e) => { if (!li.contains(e.relatedTarget) && this.current === li) this.closeAll(); });
      });
      // plain links in the nav close an open panel quickly
      $$('.nav-menu > li:not([data-mega-item])', this).forEach((li) => li.addEventListener('pointerenter', () => {
        clearTimeout(this._open); clearTimeout(this._close);
        if (this.current) this._close = setTimeout(() => this.closeAll(), 120);
      }));
      if (this.overlay) {
        this.overlay.addEventListener('pointerenter', () => { clearTimeout(this._open); this._close = setTimeout(() => this.closeAll(), 60); });
        this.overlay.addEventListener('click', () => this.closeAll());
      }
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.current) this.closeAll(); });
      document.addEventListener('pointerdown', (e) => { if (this.current && !this.contains(e.target)) this.closeAll(); });
      window.addEventListener('blur', () => this.closeAll());
    }
    openItem(li) {
      li.querySelectorAll('.mega img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      const switching = !!this.current && this.current !== li;
      this.classList.toggle('is-switching', switching);
      clearTimeout(this._sw);
      if (switching) this._sw = setTimeout(() => this.classList.remove('is-switching'), 320);
      this.items.forEach((x) => {
        const open = x === li;
        x.classList.toggle('is-open', open);
        const a = x.querySelector('.nav-menu__link'); a && a.setAttribute('aria-expanded', open);
      });
      this.current = li;
      this.syncOverlay();
    }
    closeAll() {
      clearTimeout(this._open);
      this.items.forEach((x) => { x.classList.remove('is-open'); const a = x.querySelector('.nav-menu__link'); a && a.setAttribute('aria-expanded', 'false'); });
      this.current = null;
      this.classList.remove('is-switching');
      this.syncOverlay();
    }
    syncOverlay() { this.overlay && this.overlay.classList.toggle('is-active', !!this.current); }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- announcement rotator ---------- */
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.items = $$('.announcement__item', this);
      this.i = 0;
      if (this.items.length < 2) { $$('.announcement__arrow', this).forEach((b) => (b.hidden = true)); return; }
      this.querySelector('[data-next]').addEventListener('click', () => this.go(this.i + 1, true));
      this.querySelector('[data-prev]').addEventListener('click', () => this.go(this.i - 1, true));
      this.start();
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', () => this.start());
    }
    start() { clearInterval(this.timer); if (!reduceMotion) this.timer = setInterval(() => this.go(this.i + 1), Number(this.dataset.interval) || 5000); }
    go(n) {
      const prev = this.items[this.i];
      this.i = (n + this.items.length) % this.items.length;
      prev.classList.remove('is-active'); prev.classList.add('is-leaving');
      setTimeout(() => prev.classList.remove('is-leaving'), 700);
      this.items[this.i].classList.add('is-active');
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* ---------- tabs ---------- */
  class TabsComponent extends HTMLElement {
    connectedCallback() {
      this.tabs = $$('[role="tab"]', this).filter((t) => t.closest('tabs-component') === this);
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length; this.select(n); this.tabs[n].focus(); }
        });
      });
    }
    select(i) {
      this.tabs.forEach((t, j) => {
        t.setAttribute('aria-selected', i === j);
        t.tabIndex = i === j ? 0 : -1;
        const p = document.getElementById(t.getAttribute('aria-controls'));
        if (p && i === j) p.querySelectorAll(':scope > template[data-lazy]').forEach((tpl) => tpl.replaceWith(tpl.content.cloneNode(true)));
        if (p) { p.hidden = i !== j; if (i === j) { p.classList.remove('is-entering'); void p.offsetWidth; p.classList.add('is-entering'); $$('.reveal, .reveal-img', p).forEach((r) => r.classList.add('is-revealed')); p.querySelectorAll('carousel-slider').forEach((c) => c.update && c.update()); } }
      });
    }
  }
  customElements.define('tabs-component', TabsComponent);

  /* ---------- carousel ---------- */
  class CarouselSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.bar = this.querySelector('[data-progress]');
      this.prev && this.prev.addEventListener('click', () => this.scroll(-1));
      this.next && this.next.addEventListener('click', () => this.scroll(1));
      this.track.addEventListener('scroll', () => requestAnimationFrame(() => this.update()), { passive: true });
      window.addEventListener('resize', debounce(() => this.update(), 150));
      this.update();
    }
    scroll(dir) { const item = this.track.firstElementChild; const step = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0) : 300; this.track.scrollBy({ left: dir * step * Math.max(1, Math.floor(this.track.clientWidth / step)), behavior: reduceMotion ? 'auto' : 'smooth' }); }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      if (this.prev) this.prev.disabled = x <= 4;
      if (this.next) this.next.disabled = x >= max - 4;
      if (this.bar) {
        const vis = this.track.scrollWidth ? this.track.clientWidth / this.track.scrollWidth : 1;
        this.bar.style.width = `${Math.min(100, vis * 100)}%`;
        this.bar.style.transform = `translateX(${max > 0 ? (x / max) * ((1 / Math.min(1, vis)) - 1) * 100 : 0}%)`;
      }
      const foot = this.querySelector('.showcase__foot');
      if (foot) foot.classList.toggle('is-static', max <= 4);
    }
  }
  customElements.define('carousel-slider', CarouselSlider);

  /* ---------- scroll row: arrows for horizontal rows (chips, tabs, strips) ---------- */
  const ARROW = (d) => `<svg class="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${d === 'prev' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}"/></svg>`;
  class ScrollRow extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.track = this.querySelector('[data-scroll-track]') || this.firstElementChild;
      if (!this.track) return;
      this._ready = true;
      ['prev', 'next'].forEach((dir) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = `scroll-row__btn scroll-row__btn--${dir}`;
        b.tabIndex = -1;
        b.setAttribute('aria-hidden', 'true');
        b.innerHTML = ARROW(dir);
        b.addEventListener('click', () => this.track.scrollBy({ left: (dir === 'prev' ? -1 : 1) * Math.max(160, this.track.clientWidth * 0.75), behavior: reduceMotion ? 'auto' : 'smooth' }));
        this.appendChild(b);
      });
      this.track.addEventListener('scroll', () => requestAnimationFrame(() => this.update()), { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(() => this.update()).observe(this.track);
      this.update();
    }
    update() {
      if (!this.track) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = Math.abs(this.track.scrollLeft);
      this.classList.toggle('can-prev', x > 4);
      this.classList.toggle('can-next', x < max - 4);
    }
  }
  customElements.define('scroll-row', ScrollRow);

  /* ---------- quantity ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((b) => b.addEventListener('click', () => {
        const min = Number(this.input.min || 0);
        const v = Math.max(min, Number(this.input.value || 0) + (b.name === 'plus' ? 1 : -1));
        if (String(v) === this.input.value) return;
        this.input.value = v;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- cart ---------- */
  const Cart = {
    sections() { return theme.cartType === 'drawer' && $('bag-drawer') ? ['cart-drawer'] : []; },
    async request(url, body, { sections: withSections = true } = {}) {
      const sections = withSections ? this.sections() : [];
      const payload = body instanceof FormData ? body : JSON.stringify({ ...body, sections, sections_url: location.pathname });
      if (body instanceof FormData && sections.length) { body.append('sections', sections.join(',')); body.append('sections_url', location.pathname); }
      const res = await fetch(url, { method: 'POST', headers: body instanceof FormData ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } : { 'Content-Type': 'application/json', Accept: 'application/json' }, body: payload });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
      return data;
    },
    // Quick adds (cards) don't ask for the bag drawer in the add itself, so the answer comes back sooner; the drawer is
    // refreshed right after, in the background. The bag (count and totals) is fetched once and kept in Cart.last.
    async add(formData, opts = {}) {
      const added = Number(formData.get('quantity')) || 1;
      const data = await this.request(`${theme.routes.cartAdd}.js`, formData, { sections: !opts.quick });
      await this.after(data, opts.open !== false, added);
      if (opts.quick) { this.drawerStale = true; this.refreshDrawer(); }
      return data;
    },
    // Shopify can answer a drawer request made right after an add with the bag as it was, so the drawer is only taken when its
    // count matches the bag; otherwise it asks again (a few times), and opening the bag asks once more.
    async refreshDrawer(tries = 4) {
      if (!this.sections().length) return;
      try {
        // No Accept header: on a product address Shopify would answer with the product's JSON instead of the sections
        const res = await fetch(`${location.pathname}?sections=cart-drawer`);
        const json = await res.json();
        const html = json && json['cart-drawer'];
        if (!html) return;
        const count = Number((new DOMParser().parseFromString(html, 'text/html').querySelector('[data-item-count]') || {}).dataset?.itemCount);
        const want = Number.isFinite(this.expected) ? this.expected : this.last && this.last.item_count;
        if (Number.isFinite(want) && count !== want && tries > 1) { setTimeout(() => this.refreshDrawer(tries - 1), 500); return; }
        this.renderDrawer(html);
        this.drawerStale = false;
      } catch (e) { /* the drawer refreshes on the next change */ }
    },
    async change(line, quantity) {
      const data = await this.request(`${theme.routes.cartChange}.js`, { line, quantity });
      await this.after(data, false);
      return data;
    },
    async after(data, opened, added = 1) {
      BagPop && BagPop.hide && opened && BagPop.hide();
      let count = null;
      if (data.sections && data.sections['cart-drawer']) {
        this.renderDrawer(data.sections['cart-drawer']); this.drawerStale = false;
        const shown = $('bag-drawer [data-item-count]'); if (shown) count = Number(shown.dataset.itemCount);
      }
      const settle = (cart) => {
        this.last = cart; this.expected = cart.item_count;
        if (cart.item_count !== count) this.updateCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        return cart;
      };
      if (typeof data.item_count === 'number') {
        this.last = data; this.expected = data.item_count; this.updateCount(data.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      }
      else {
        // An add answers with the added line only. The bag opens / the confirmation shows straight away with the count the
        // bag will have (from the drawer just rendered, or the last count plus what was added); the full bag (/cart.js)
        // follows in the background and corrects the count if needed.
        if (!Number.isFinite(count)) count = (this.last ? this.last.item_count : Number(($('[data-cart-count]') || {}).dataset?.count) || 0) + added;
        this.updateCount(count); this.expected = count;
        this.pending = fetch(`${theme.routes.cart}.js`).then((r) => r.json()).then(settle).catch(() => {});
      }
      if (opened) {
        const drawer = $('bag-drawer');
        if (drawer && theme.cartType === 'drawer') drawer.open(); else if (theme.cartType === 'page') location.href = theme.routes.cart; else toast(theme.strings.added);
      }
    },
    renderDrawer(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-drawer-content]');
      const current = $('bag-drawer [data-cart-drawer-content]');
      if (!fresh || !current) return;
      const scroller = $('[data-cart-scroll]', current);
      const top = scroller ? scroller.scrollTop : 0;
      current.innerHTML = fresh.innerHTML;
      const next = $('[data-cart-scroll]', current);
      if (next) next.scrollTop = top;
    },
    updateCount(n) {
      $$('[data-cart-count]').forEach((el) => { el.dataset.count = n; el.textContent = n > 0 ? n : ''; el.classList.remove('bump'); void el.offsetWidth; el.classList.add('bump'); });
      $$('[data-cart-count-text]').forEach((el) => { el.textContent = n; });
    },
  };
  window.ManTireCart = Cart;

  // Luxury look: searching keeps to luxury-priced fabrics (the results page then opens in Luxury too).
  const luxSearch = () => document.documentElement.dataset.mode === 'luxury' && theme.luxuryFrom ? String(theme.luxuryFrom / 100) : '';
  document.addEventListener('submit', (e) => {
    const form = e.target;
    const from = luxSearch();
    if (!from || !form.matches('form[role="search"]') || form.querySelector('[name="filter.v.price.gte"]')) return;
    const input = document.createElement('input');
    input.type = 'hidden'; input.name = 'filter.v.price.gte'; input.value = from;
    form.appendChild(input);
  }, true);
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href]');
    const from = a && luxSearch();
    if (!from) return;
    const u = new URL(a.href, location.origin);
    if (u.origin !== location.origin || u.pathname !== theme.routes.search || u.searchParams.has('filter.v.price.gte') || a.closest('[data-all-prices]')) return;
    u.searchParams.set('filter.v.price.gte', from);
    a.href = u.pathname + u.search;
  }, true);

  // Chips whose listing is empty right now are hidden ([data-empty-check]: budget bands, Luxury related chips), and
  // budget steps with a filtered link show that listing's real count ([data-live-count]). One small request per link, after load.
  const listingCount = async (href) => {
    const u = new URL(href, location.origin); u.searchParams.set('section_id', 'finder-results');
    const el = new DOMParser().parseFromString(await (await fetch(u)).text(), 'text/html').querySelector('[data-finder-results]');
    return el ? Number(el.dataset.count) : null;
  };
  // (not for a guide or budget row that is hidden at this screen size: phones show the budget row, larger screens the guide)
  const luxChecks = $$('[data-empty-check], [data-live-count]').filter((a) => { const g = a.closest('.cguide, .collection-hero__budget'); return !g || getComputedStyle(g).display !== 'none'; });
  if (luxChecks.length && window.fetch) {
    const run = () => luxChecks.forEach(async (a) => {
      try {
        if (a.dataset.liveCount) {
          const n = await listingCount(a.dataset.liveCount);
          if (n != null) a.textContent = (n === 1 ? a.dataset.one : a.dataset.other).replace('[n]', n.toLocaleString('en-IN'));
          return;
        }
        if (await listingCount(a.href) === 0) { a.hidden = true; const row = a.closest('scroll-row'); if (row && row.update) row.update(); }
      } catch (e) { /* keep the chip */ }
    });
    'requestIdleCallback' in window ? requestIdleCallback(run, { timeout: 2000 }) : setTimeout(run, 600);
  }

  // GoKwik checkout (snippets/gokwik.liquid). Checkout and Buy now open GoKwik once its script has loaded.
  // If it is still loading we wait up to six seconds; if it never loads (blocked, offline, switched off) Shopify's checkout takes over.
  const GoKwik = {
    on: () => !!(window.merchantInfo && window.merchantInfo.mid),
    ready(ms = 6000) {
      if (window.gokwikReady) return Promise.resolve(true);
      // GoKwik only starts loading its checkout once the whole page has loaded; start it now (the same call its own script makes then)
      try { if (window.gokwikSdk && typeof window.gokwikSdk.init === 'function') window.gokwikSdk.init(); } catch (err) { /* its own load will still run */ }
      return new Promise((resolve) => {
        const done = (ok) => { clearTimeout(t); window.removeEventListener('gokwikLoaded', hit); resolve(ok); };
        const hit = () => done(true);
        const t = setTimeout(() => done(!!window.gokwikReady), ms);
        window.addEventListener('gokwikLoaded', hit);
      });
    },
    busy(btn, on) {
      const spin = btn.querySelector('.gk-loader');
      if (spin) spin.style.display = on ? 'flex' : '';
      GoKwik.watch(btn);
      btn.toggleAttribute('aria-busy', on);
    },
    // GoKwik's onCheckoutClick/onBuyNowClick take the button's last child as their loader and show it (style display flex)
    // for a few seconds, so every Checkout / Buy now button ends with a .gk-loader. The button looks busy (base.css
    // .is-gk-busy) exactly while that spinner shows, whoever shows it.
    watch(btn) {
      const spin = btn && btn.querySelector(':scope > .gk-loader');
      if (!spin) return;
      const sync = () => btn.classList.toggle('is-gk-busy', (spin.getAttribute('style') || '').includes('flex'));
      sync();
      if (!spin.gkWatched) { spin.gkWatched = true; new MutationObserver(sync).observe(spin, { attributes: true, attributeFilter: ['style'] }); }
    },
  };
  $$('.button > .gk-loader').forEach((spin) => GoKwik.watch(spin.parentElement));
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('button[name="checkout"]');
    if (!btn || !GoKwik.on() || btn.hasAttribute('aria-busy')) return;
    const form = btn.form;
    if (!form) return;
    e.preventDefault();
    GoKwik.busy(btn, true);
    // The order note is saved first only when the customer changed it (otherwise it is already on the bag)
    const note = form.elements.note;
    if (note instanceof HTMLTextAreaElement && note.value !== note.defaultValue) await fetch(`${theme.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) }).catch(() => {});
    const ok = await GoKwik.ready() && typeof window.onCheckoutClick === 'function';
    GoKwik.busy(btn, false);
    if (ok) { window.onCheckoutClick(btn); return; }
    form.requestSubmit ? form.requestSubmit(btn) : form.submit();
  });
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-buy-now]');
    if (!btn || btn.disabled || btn.hasAttribute('aria-busy')) return;
    const form = btn.form || btn.closest('form');
    if (!form || !form.elements.id) return;
    if (GoKwik.on()) {
      GoKwik.busy(btn, true);
      const ok = await GoKwik.ready() && typeof window.onBuyNowClick === 'function';
      GoKwik.busy(btn, false);
      if (ok) { window.productFormSelector = `#${CSS.escape(form.getAttribute('id'))}`; window.onBuyNowClick(btn); return; } // getAttribute: form.id is the variant input named "id"
    }
    // Shopify checkout with just this fabric (a cart link, so the bag is left as it is)
    const qty = Math.max(1, parseInt(form.elements.quantity && form.elements.quantity.value, 10) || 1);
    location.href = `${theme.routes.root.replace(/\/$/, '')}/cart/${form.elements.id.value}:${qty}`;
  });

  class CartDrawer extends DrawerPanel {
    open(opener) {
      super.open(opener);
      if (Cart.drawerStale) Cart.refreshDrawer();
    }
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-line-qty]');
        if (input) this.update(input.dataset.lineQty, Number(input.value));
      });
      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-line-remove]');
        if (rm) { e.preventDefault(); this.update(rm.dataset.lineRemove, 0); }
      });
      this.addEventListener('input', debounce((e) => {
        if (e.target.name === 'note') fetch(`${theme.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: e.target.value }) });
      }, 400));
    }
    async update(line, qty) {
      const row = this.querySelector(`[data-cart-line="${line}"]`);
      row && row.classList.add('is-updating');
      try { await Cart.change(Number(line), qty); } catch (err) { toast(err.message); row && row.classList.remove('is-updating'); }
    }
  }
  customElements.define('bag-drawer', CartDrawer);

  /* visible bag icon (header, stuck nav or tab bar) for the fly-to-bag animation */
  const bagTarget = () => $$('[data-cart-open]').map((el) => el.querySelector('.icon--bag') || el).find((el) => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.bottom > 0 && r.top < window.innerHeight && getComputedStyle(el).visibility !== 'hidden' && el.closest('.bag-pop') === null;
  });
  const flyToBag = (fromEl) => {
    if (reduceMotion || !fromEl || !Element.prototype.animate) return Promise.resolve();
    const target = bagTarget();
    const src = fromEl.querySelector('img');
    if (!target || !src) return Promise.resolve();
    const a = fromEl.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    const dot = document.createElement('div');
    dot.className = 'fly-dot';
    const img = document.createElement('img'); img.src = src.currentSrc || src.src; img.alt = '';
    dot.appendChild(img);
    document.body.appendChild(dot);
    const size = 72;
    const x0 = a.left + a.width / 2 - size / 2; const y0 = a.top + a.height / 2 - size / 2;
    const x1 = b.left + b.width / 2 - size / 2; const y1 = b.top + b.height / 2 - size / 2;
    const lift = Math.min(160, Math.abs(y1 - y0) * 0.4 + 60);
    const anim = dot.animate([
      { transform: `translate(${x0}px, ${y0}px) scale(1.25)`, opacity: 0 },
      { transform: `translate(${x0}px, ${y0}px) scale(1)`, opacity: 1, offset: 0.12 },
      { transform: `translate(${(x0 + x1) / 2}px, ${Math.min(y0, y1) - lift}px) scale(.7)`, opacity: 1, offset: 0.55 },
      { transform: `translate(${x1}px, ${y1}px) scale(.18)`, opacity: .4 },
    ], { duration: 820, easing: 'cubic-bezier(.5, 0, .2, 1)' });
    return anim.finished.then(() => {
      dot.remove();
      const host = target.closest('[data-cart-open]') || target;
      host.classList.remove('is-catching'); void host.offsetWidth; host.classList.add('is-catching');
    }).catch(() => dot.remove());
  };

  const BagPop = {
    el: null,
    show(item, cart) {
      this.el = this.el || $('[data-bag-pop]');
      const el = this.el; if (!el || !item) return;
      const img = $('[data-bag-pop-img]', el);
      const src = item.featured_image && item.featured_image.url ? item.featured_image.url : item.image;
      if (src) { img.src = sizedImage(src, 160); img.hidden = false; } else img.hidden = true;
      $('[data-bag-pop-title]', el).textContent = item.product_title || item.title || '';
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `${item.variant_title} · ` : '';
      $('[data-bag-pop-meta]', el).innerHTML = '';
      const meta = $('[data-bag-pop-meta]', el);
      meta.append(document.createTextNode(variant));
      const b = document.createElement('b'); b.textContent = formatMoney(item.final_price || item.price); meta.append(b);
      if (cart) $$('[data-cart-count-text]', el).forEach((n) => { n.textContent = cart.item_count; });
      // keep clear of the sticky header on desktop
      const header = $('.site-header');
      if (header) el.style.setProperty('--pop-top', `${Math.max(12, header.getBoundingClientRect().bottom + 12)}px`);
      el.hidden = false;
      requestAnimationFrame(() => el.classList.add('is-visible'));
      clearTimeout(this.t);
      const life = 5000;
      el.style.setProperty('--pop-life', `${life}ms`);
      const bar = $('.bag-pop__bar', el); if (bar) { bar.style.animation = 'none'; void bar.offsetWidth; bar.style.animation = ''; }
      this.t = setTimeout(() => this.hide(), life);
      if (!this._bound) {
        this._bound = true;
        el.addEventListener('mouseenter', () => clearTimeout(this.t));
        el.addEventListener('mouseleave', () => { this.t = setTimeout(() => this.hide(), 2200); });
        el.addEventListener('click', (e) => { if (e.target.closest('[data-bag-pop-close]') || e.target.closest('[data-cart-open]')) this.hide(); });
        document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.hide(); });
      }
    },
    hide() { if (!this.el) return; this.el.classList.remove('is-visible'); clearTimeout(this.t); },
  };

  /* quick add on cards and in the recently viewed drawer; full product form on the product page */
  // Handled on window in the capture phase, so cart apps listening on the page (Bob's cart drawer) don't take over the theme's add to bag.
  window.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-quick-add], [data-product-form]');
    if (!form || !window.fetch) return;
    e.preventDefault(); e.stopImmediatePropagation();
    const btn = e.submitter || form.querySelector('[type="submit"]');
    const quick = form.matches('[data-quick-add]');
    const fd = new FormData(form);
    if (btn && btn.name && btn.value) fd.set(btn.name, btn.value);
    if (btn) btn.classList.add('is-loading');
    try {
      const card = form.closest('[data-product-card], .recent-item');
      const media = card && card.querySelector('.card__media, .recent-item__media');
      const data = await Cart.add(fd, { open: !quick, quick });
      const item = Array.isArray(data.items) ? data.items[0] : data;
      if (quick) {
        // Done: tick on the button and the confirmation straight away; the fabric flies to the bag alongside.
        if (btn) { btn.classList.remove('is-loading'); btn.classList.add('is-added'); }
        form.classList.add('is-added');
        BagPop.show(item, null);
        setTimeout(() => { form.classList.remove('is-added'); btn && btn.classList.remove('is-added'); const c = form.closest('.card'); c && c.classList.remove('is-picking'); }, 1800);
        const tucked = $('.section-header.is-tucked');
        if (tucked) { tucked.classList.remove('is-tucked'); setTimeout(() => flyToBag(media), 380); } else flyToBag(media);
      }
    } catch (err) { toast(err.message || theme.strings.error); }
    finally { if (btn) btn.classList.remove('is-loading'); }
  }, true);

  /* phones: the + on a multi-length card opens the length picker in place */
  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-quick-open]');
    if (open) { e.preventDefault(); const c = open.closest('.card'); $$('.card.is-picking').forEach((x) => x !== c && x.classList.remove('is-picking')); c && c.classList.add('is-picking'); return; }
    const close = e.target.closest('[data-quick-close]');
    if (close) { e.preventDefault(); const c = close.closest('.card'); c && c.classList.remove('is-picking'); return; }
    if (!e.target.closest('.card.is-picking')) $$('.card.is-picking').forEach((x) => x.classList.remove('is-picking'));
  });

  class CartPage extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-line-qty]');
        if (input) this.update(Number(input.dataset.lineQty), Number(input.value));
      });
      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-line-remove]');
        if (rm) { e.preventDefault(); this.update(Number(rm.dataset.lineRemove), 0); }
      });
      this.addEventListener('input', debounce((e) => {
        if (e.target.matches('[data-cart-note]')) fetch(`${theme.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: e.target.value }) });
      }, 400));
    }
    async update(line, quantity) {
      this.classList.add('is-updating');
      try {
        const res = await fetch(`${theme.routes.cartChange}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity }) });
        const cart = await res.json();
        Cart.updateCount(cart.item_count);
        await this.refresh();
      } catch (err) { toast(theme.strings.error); }
      finally { this.classList.remove('is-updating'); }
    }
    async refresh() {
      const html = await (await fetch(`${theme.routes.cart}?section_id=${this.dataset.sectionId}`)).text();
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-page');
      if (fresh) this.innerHTML = fresh.innerHTML;
    }
  }
  customElements.define('cart-page', CartPage);

  /* ---------- discount codes in the bag ---------- */
  const Discount = {
    current(root) {
      const box = root && $('[data-cart-discount]', root);
      return box && box.dataset.codes ? box.dataset.codes.split(',').map((x) => x.trim()).filter(Boolean) : [];
    },
    // Shopify returns discount_codes [{ code, applicable }]; older responses only list the applications.
    accepted(cart, code) {
      const want = String(code).toUpperCase();
      if (Array.isArray(cart.discount_codes)) return cart.discount_codes.some((d) => String(d.code).toUpperCase() === want && d.applicable);
      const titles = [];
      (cart.cart_level_discount_applications || []).forEach((a) => a.type === 'discount_code' && titles.push(a.title));
      (cart.items || []).forEach((it) => (it.line_level_discount_allocations || []).forEach((al) => { const a = al.discount_application; if (a && a.type === 'discount_code') titles.push(a.title); }));
      return titles.some((t) => String(t).toUpperCase() === want) ? true : null; // null: can't tell here, checkout will confirm
    },
    async send(codes) {
      const data = await Cart.request(`${theme.routes.cartUpdate}.js`, { discount: codes.join(',') });
      await Cart.after(data, false);
      const page = $('cart-page');
      if (page && page.refresh) await page.refresh();
      return data;
    },
    say(root, text, ok) {
      const el = $('[data-discount-msg]', root);
      if (!el) return;
      el.hidden = !text;
      el.textContent = text || '';
      el.classList.toggle('is-error', ok === false);
      el.classList.toggle('is-success', ok === true);
    },
    async apply(root, code) {
      code = String(code || '').trim();
      if (!code) { this.say(root, theme.strings.discountEmpty, false); return; }
      const before = this.current(root);
      if (before.some((c) => c.toUpperCase() === code.toUpperCase())) { this.say(root, theme.strings.discountApplied.replace('[code]', code.toUpperCase()), true); return; }
      const btn = $('.cart-discount__apply', root);
      if (btn) btn.classList.add('is-loading');
      try {
        const cart = await this.send([...before, code]);
        const ok = this.accepted(cart, code);
        if (ok === false) {
          await this.send(before); // don't carry a code that doesn't apply into checkout
          this.say(root, theme.strings.discountInvalid.replace('[code]', code.toUpperCase()), false);
        } else {
          this.say(root, theme.strings.discountApplied.replace('[code]', code.toUpperCase()), true);
        }
      } catch { this.say(root, theme.strings.discountError, false); const b = $('.cart-discount__apply', root); b && b.classList.remove('is-loading'); }
    },
    async remove(root, code) {
      const rest = this.current(root).filter((c) => c.toUpperCase() !== String(code).toUpperCase());
      try { await this.send(rest); } catch { this.say(root, theme.strings.discountError, false); }
    },
  };
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-discount-form]');
    if (!form) return;
    e.preventDefault();
    const root = form.closest('bag-drawer, cart-page');
    Discount.apply(root, form.querySelector('[data-discount-input]').value);
  });
  document.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-remove-code]');
    if (rm) { e.preventDefault(); Discount.remove(rm.closest('bag-drawer, cart-page'), rm.dataset.removeCode); return; }
    const offer = e.target.closest('[data-apply-code]');
    if (offer) { e.preventDefault(); Discount.apply(offer.closest('bag-drawer, cart-page'), offer.dataset.applyCode); }
  });

  /* ---------- predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.defaultHTML = this.results.innerHTML;
      if (this.dataset.enabled === 'false') return;
      this.input.addEventListener('input', debounce(() => this.search(), 220));
    }
    async search() {
      const q = this.input.value.trim();
      if (!q) { this.results.innerHTML = this.defaultHTML; return; }
      this.abort && this.abort.abort();
      this.abort = new AbortController();
      const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,article,query&resources[limit]=6&resources[options][unavailable_products]=last&section_id=${this.dataset.sectionId}`;
      try {
        const html = await (await fetch(url, { signal: this.abort.signal })).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-predictive]');
        // Luxury look: suggest luxury-priced fabrics only
        if (fresh && document.documentElement.dataset.mode === 'luxury' && theme.luxuryFrom) {
          fresh.querySelectorAll('.predictive__products .mini-card[data-price-max]').forEach((c) => { if (Number(c.dataset.priceMax) < theme.luxuryFrom) c.closest('li').remove(); });
          const list = fresh.querySelector('.predictive__products');
          if (list && !list.children.length) { list.remove(); const label = fresh.querySelector('[data-products-label]'); if (label) label.remove(); }
        }
        if (fresh) this.results.innerHTML = fresh.outerHTML;
      } catch (err) { if (err.name !== 'AbortError') this.results.innerHTML = this.defaultHTML; }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- fabric finder ---------- */
  class FabricFinder extends HTMLElement {
    connectedCallback() {
      try { this.config = JSON.parse(this.querySelector('[data-finder-config]').textContent); } catch { return; }
      this.state = {};
      this.steps = ['garment', 'budget', 'fabric', 'pattern'];
      this.go = this.querySelector('[data-go]');
      this.countEl = this.querySelector('[data-count]');
      this.preview = this.querySelector('[data-preview]');
      this.summary = this.querySelector('[data-summary]');
      this.querySelector('[data-back]').addEventListener('click', () => this.back());
      this.querySelector('[data-reset]').addEventListener('click', () => { this.state = {}; this.renderGarments(); this.show('garment'); this.refresh(); });
      this.renderGarments();
      this.show('garment');
      this.fetchCount = debounce(() => this.loadResults(), 250);
    }
    // Back undoes the answer to the question you return to (and any after it), so the summary, count and progress match what is on screen
    back() {
      const i = this.steps.indexOf(this.current);
      if (i <= 0) return;
      const prev = this.steps[i - 1];
      if (prev === 'garment') { this.state = {}; this.renderGarments(); } else { this.steps.slice(i - 1).forEach((s) => { delete this.state[s]; }); this.renderStep(prev); }
      this.show(prev);
      this.refresh();
    }
    t(key, vars = {}) { return (theme.strings[key] || '').replace(/{{\s*(\w+)\s*}}/g, (_, k) => vars[k] ?? ''); }
    option(label, hint, pressed, onClick, extra = '') {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `finder-opt ${extra}`;
      b.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      b.innerHTML = `<span></span>${hint ? '<small></small>' : ''}`;
      b.querySelector('span').textContent = label;
      if (hint) b.querySelector('small').textContent = hint;
      b.addEventListener('click', onClick);
      return b;
    }
    renderGarments() {
      const box = this.querySelector('[data-options="garment"]');
      box.innerHTML = '';
      this.config.garments.forEach((g) => {
        const b = this.option(g.label, g.hint, this.state.garment === g, () => { this.state = { garment: g }; this.renderGarments(); this.renderStep('budget'); this.show('budget'); this.refresh(); }, 'finder-opt--garment');
        if (g.image) { const img = document.createElement('img'); img.src = g.image; img.alt = ''; img.loading = 'lazy'; img.width = 56; img.height = 56; b.prepend(img); }
        box.appendChild(b);
      });
    }
    renderStep(step) {
      const g = this.state.garment; if (!g) return;
      const box = this.querySelector(`[data-options="${step}"]`);
      box.innerHTML = '';
      const list = step === 'budget' ? g.budgets : step === 'fabric' ? g.fabrics : g.patterns;
      const next = this.steps[this.steps.indexOf(step) + 1];
      const choose = (val) => {
        this.state[step] = val;
        this.renderStep(step);
        const nextList = next && (next === 'fabric' ? g.fabrics : g.patterns);
        if (next && nextList && nextList.length) { this.renderStep(next); this.show(next); } else { this.show(step); }
        this.refresh();
      };
      list.forEach((o) => box.appendChild(this.option(o.label, o.hint, this.state[step] === o, () => choose(o))));
      const anyLabel = step === 'budget' ? (this.dataset.anyBudget || 'No fixed budget') : (this.dataset.notSure || 'Not sure — help me choose');
      box.appendChild(this.option(anyLabel, '', this.state[step] === null, () => choose(null), 'finder-opt--any'));
    }
    show(step) {
      this.current = step;
      $$('.finder__step', this).forEach((s) => s.classList.toggle('is-active', s.dataset.step === step));
      const idx = this.steps.indexOf(step);
      $$('[data-progress]', this).forEach((li) => {
        const i = this.steps.indexOf(li.dataset.progress);
        li.classList.toggle('is-current', i === idx);
        li.classList.toggle('is-done', i < idx || (this.state[li.dataset.progress] !== undefined && li.dataset.progress !== 'garment' ? true : li.dataset.progress === 'garment' && !!this.state.garment && i < idx));
      });
      this.querySelector('[data-back]').hidden = idx === 0;
      this.querySelector('[data-reset]').hidden = !this.state.garment;
    }
    buildUrl() {
      const g = this.state.garment; if (!g) return null;
      const f = this.state.fabric;
      const b = this.state.budget;
      // Budgets from the luxury price search the garment's luxury edit; a fabric with its own collection still wins
      const luxFrom = ((window.theme && theme.luxuryFrom) || 400000) / 100;
      const lux = b && b.min && Number(b.min) >= luxFrom && g.luxUrl;
      // and the budget just under it (marked "prem", as in the menu) the garment's Premium edit
      const prem = b && b.prem && g.premUrl;
      const u = new URL((f && f.url) || (lux ? g.luxUrl : (prem ? g.premUrl : g.url)), location.origin);
      if (b) { if (b.min) u.searchParams.set('filter.v.price.gte', b.min); if (b.max) u.searchParams.set('filter.v.price.lte', b.max); }
      if (f && f.value) u.searchParams.append(this.config.fabricParam, f.value);
      const p = this.state.pattern;
      if (p && p.value) u.searchParams.append(this.config.patternParam, p.value);
      return u;
    }
    refresh() {
      const g = this.state.garment;
      this.summary.innerHTML = '';
      const labels = { garment: 'Garment', budget: 'Budget', fabric: 'Fabric', pattern: 'Pattern' };
      this.steps.forEach((s) => {
        const v = this.state[s];
        if (v === undefined) return;
        const pill = document.createElement('span');
        pill.className = 'finder__pill';
        pill.innerHTML = '<b></b><span></span>';
        pill.querySelector('b').textContent = labels[s];
        pill.querySelector('span').textContent = v ? v.label : '—';
        this.summary.appendChild(pill);
      });
      const url = this.buildUrl();
      if (!url) { this.go.setAttribute('aria-disabled', 'true'); this.countEl.textContent = ''; this.preview.hidden = true; return; }
      this.go.href = url.pathname + url.search;
      this.go.removeAttribute('aria-disabled');
      this.countEl.classList.add('is-loading');
      this.fetchCount();
    }
    async loadResults() {
      const url = this.buildUrl(); if (!url) return;
      url.searchParams.set('section_id', this.dataset.resultsSection || 'finder-results');
      const key = url.toString();
      this._key = key;
      try {
        const html = await (await fetch(key)).text();
        if (this._key !== key) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const res = doc.querySelector('[data-finder-results]');
        const n = res ? Number(res.dataset.count) : 0;
        this.countEl.classList.remove('is-loading');
        this.countEl.innerHTML = n > 0 ? `<span class="num">${n.toLocaleString('en-IN')}</span> ${this.t('matches', { count: '' }).replace(/^\s*/, '').trim()}` : this.t('noMatches');
        const list = res && res.querySelector('.finder__preview-list');
        this.preview.hidden = !list;
        this.preview.innerHTML = list ? list.outerHTML : '';
      } catch { this.countEl.classList.remove('is-loading'); }
    }
  }
  customElements.define('fabric-finder', FabricFinder);

  /* ---------- collection guide: search by → choose → budget → matching fabrics ---------- */
  class CollectionGuide extends HTMLElement {
    connectedCallback() {
      this.state = { dim: null, dimLabel: '', param: null, value: null, label: '', min: '', max: '', band: '', base: '' };
      // Price opens already when the page is one of its budgets
      const open = this.querySelector('[data-step="dim"] .cguide__chip[aria-pressed="true"]');
      if (open) this.state = { ...this.state, dim: open.dataset.dim, dimLabel: open.textContent.trim() };
      this.result = this.querySelector('[data-result]');
      this.status = this.querySelector('[data-status]');
      this.go = this.querySelector('[data-go]');
      this.goLabel = this.querySelector('[data-go-label]');
      this.addEventListener('click', (e) => {
        const chip = e.target.closest('.cguide__chip');
        if (chip) { this.pick(chip); return; }
        if (e.target.closest('[data-reset]')) this.clear();
        if (this.go.contains(e.target) && this.go.getAttribute('aria-disabled') === 'true') e.preventDefault();
      });
      this.count = debounce(() => this.fetchCount(), 200);
    }
    pick(chip) {
      const step = chip.closest('[data-step]').dataset.step;
      const press = (scope, el) => $$('.cguide__chip', scope).forEach((c) => c.setAttribute('aria-pressed', c === el ? 'true' : 'false'));
      const reveal = (s) => { if (!s) return; s.hidden = false; s.classList.remove('is-entering'); void s.offsetWidth; s.classList.add('is-entering'); const r = $('scroll-row', s); r && r.update && r.update(); };
      if (step === 'dim') {
        const same = this.state.dim === chip.dataset.dim;
        this.state = { ...this.state, dim: same ? null : chip.dataset.dim, dimLabel: same ? '' : chip.textContent.trim(), param: null, value: null, label: '' };
        press(chip.closest('[data-step]'), same ? null : chip);
        $$('[data-step="value"]', this).forEach((s) => { if (same && !s.hidden) this.collapse(s); else s.hidden = true; press(s, null); });
        if (!same) reveal($(`[data-step="value"][data-for="${this.state.dim}"]`, this));
        // Price shows the Shop by budget chips, which open their listing straight away; the budget step below is for a picked brand, fabric…
        const budgetStep = $('[data-step="budget"]', this);
        if (this.state.dim === 'price' && budgetStep) { budgetStep.hidden = true; press(budgetStep, null); this.state = { ...this.state, min: '', max: '', band: '', base: '' }; }
      } else if (step === 'value') {
        const same = this.state.value === chip.dataset.value;
        this.state = { ...this.state, param: same ? null : chip.dataset.param, value: same ? null : chip.dataset.value, label: same ? '' : chip.dataset.label };
        press(chip.closest('[data-step]'), same ? null : chip);
      } else if (step === 'budget') {
        const same = this.state.band === chip.dataset.label;
        this.state = { ...this.state, min: same ? '' : chip.dataset.min, max: same ? '' : chip.dataset.max, band: same ? '' : chip.dataset.label, base: same ? '' : (chip.dataset.base || '') };
        press(chip.closest('[data-step]'), same ? null : chip);
      }
      const budget = $('[data-step="budget"]', this);
      if (budget && this.state.value && budget.hidden) reveal(budget);
      if (step !== 'budget') this.checkBands();
      this.refresh();
    }
    // Folds a step away (height and fade), then hides it
    collapse(step) {
      if (step.hidden) return;
      const done = () => { step.hidden = true; step.style.overflow = ''; };
      if (!step.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { done(); return; }
      step.style.overflow = 'hidden';
      const a = step.animate([{ height: `${step.offsetHeight}px`, opacity: 1, marginTop: '0px' }, { height: '0px', opacity: 0, marginTop: '-14px' }], { duration: 300, easing: 'cubic-bezier(.2, .7, .2, 1)', fill: 'forwards' });
      a.onfinish = () => { done(); a.cancel(); };
    }
    // The Price step follows the listing: it opens when the page lands on a budget (e.g. after Back), and closes smoothly when the budget is cleared from its chip
    syncPrice(cleared) {
      const step = $('[data-step="value"][data-for="price"]', this); const chip = $('[data-dim="price"]', this);
      if (!step || !chip) return;
      const onBudget = !!$('a.chip[aria-current="page"]', step);
      if (onBudget && (step.hidden || this.state.dim !== 'price')) this.pick(chip);
      else if (cleared && this.state.dim === 'price') this.pick(chip);
    }
    // Hide price bands with no fabrics for the chosen brand/fabric/colour, so a budget never leads to an empty page.
    async checkBands() {
      const bands = $$('[data-step="budget"] .cguide__chip', this);
      const token = (this._bands = {});
      bands.forEach((c) => { c.hidden = false; });
      if (!this.state.param || !this.state.value) return;
      const counts = await Promise.all(bands.map(async (c) => {
        const u = new URL(c.dataset.base || this.dataset.base, location.origin);
        u.searchParams.append(this.state.param, this.state.value);
        if (c.dataset.min) u.searchParams.set('filter.v.price.gte', c.dataset.min);
        if (c.dataset.max) u.searchParams.set('filter.v.price.lte', c.dataset.max);
        u.searchParams.set('section_id', this.dataset.section);
        try {
          const el = new DOMParser().parseFromString(await (await fetch(u)).text(), 'text/html').querySelector('[data-finder-results]');
          return el ? Number(el.dataset.count) : 1;
        } catch { return 1; }
      }));
      if (this._bands !== token) return;
      bands.forEach((c, i) => { c.hidden = counts[i] === 0; });
      const picked = bands.find((c) => c.hidden && c.getAttribute('aria-pressed') === 'true');
      if (picked) { picked.setAttribute('aria-pressed', 'false'); this.state = { ...this.state, min: '', max: '', band: '', base: '' }; this.refresh(); }
      const row = $('[data-step="budget"] scroll-row', this); if (row && row.update) row.update();
    }
    url(extra) {
      // The Luxury band carries its own listing (the garment's luxury edit)
      const u = new URL(this.state.base || this.dataset.base, location.origin);
      if (this.state.param && this.state.value) u.searchParams.append(this.state.param, this.state.value);
      if (this.state.min) u.searchParams.set('filter.v.price.gte', this.state.min);
      if (this.state.max) u.searchParams.set('filter.v.price.lte', this.state.max);
      if (extra) Object.entries(extra).forEach(([k, v]) => u.searchParams.set(k, v));
      return u;
    }
    refresh() {
      const active = !!(this.state.value || this.state.band);
      this.result.hidden = !(active || (this.state.dim && this.state.dim !== 'price'));
      this.goLabel.textContent = this.dataset.go;
      if (!active) {
        this.go.setAttribute('aria-disabled', 'true'); this.go.href = this.dataset.base;
        this.status.textContent = this.state.dim ? (this.dataset.pick || '').replace('[filter]', this.state.dimLabel.toLowerCase()) : '';
        return;
      }
      const u = this.url();
      this.go.href = u.pathname + u.search;
      this.go.removeAttribute('aria-disabled');
      this.status.textContent = this.dataset.checking;
      this.count();
    }
    async fetchCount() {
      const key = this.url({ section_id: this.dataset.section }).toString();
      this._key = key;
      try {
        const html = await (await fetch(key)).text();
        if (this._key !== key) return;
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-finder-results]');
        const n = el ? Number(el.dataset.count) : 0;
        const parts = [this.state.label, this.state.band].filter(Boolean).join(' · ');
        const nice = n.toLocaleString('en-IN');
        const text = n === 0 ? this.dataset.none : (n === 1 ? this.dataset.one : this.dataset.many.replace('[n]', nice));
        this.status.innerHTML = '';
        const b = document.createElement('b'); b.textContent = text; this.status.appendChild(b);
        if (parts && n > 0) { const sp = document.createElement('span'); sp.textContent = parts; this.status.appendChild(sp); }
        if (n === 0) this.go.setAttribute('aria-disabled', 'true');
        else this.goLabel.textContent = (this.dataset.goCount || this.dataset.go).replace('[n]', nice);
      } catch { this.status.textContent = ''; }
    }
    clear() {
      this.state = { dim: null, dimLabel: '', param: null, value: null, label: '', min: '', max: '', band: '', base: '' };
      $$('.cguide__chip', this).forEach((c) => c.setAttribute('aria-pressed', 'false'));
      $$('[data-step="value"], [data-step="budget"]', this).forEach((s) => { s.hidden = true; });
      this._bands = null; $$('[data-step="budget"] .cguide__chip', this).forEach((c) => { c.hidden = false; });
      this.refresh();
    }
  }
  customElements.define('collection-guide', CollectionGuide);

  // Shop by budget chips: a band of this listing loads in place (no page reload), and tapping the chosen band again clears it,
  // closing the guide's Price step. Other filters and the sort stay. Luxury opens its own listing, so it navigates as usual.
  const PRICE_KEYS = ['filter.v.price.gte', 'filter.v.price.lte'];
  const syncBudget = (url, cleared) => {
    const priced = PRICE_KEYS.some((k) => url.searchParams.get(k));
    $$('[data-budget-chips] a.chip:not(.is-lux)').forEach((a) => {
      const t = new URL(a.href, location.origin);
      const on = priced && PRICE_KEYS.every((k) => (t.searchParams.get(k) || '') === (url.searchParams.get(k) || ''));
      if (on) { a.setAttribute('aria-current', 'page'); a.hidden = false; } else a.removeAttribute('aria-current');
    });
    $$('collection-guide').forEach((g) => g.syncPrice && g.syncPrice(cleared));
  };
  document.addEventListener('facets:rendered', (e) => syncBudget(new URL(e.detail.url, location.origin), false));
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-budget-chips] a.chip');
    if (!a || a.classList.contains('is-lux') || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const target = new URL(a.href, location.origin);
    if (target.pathname !== location.pathname) return;
    e.preventDefault();
    const clearing = a.getAttribute('aria-current') === 'page';
    const u = new URL(location.href);
    [...PRICE_KEYS, 'page'].forEach((k) => u.searchParams.delete(k));
    if (!clearing) PRICE_KEYS.forEach((k) => { const v = target.searchParams.get(k); if (v) u.searchParams.set(k, v); });
    const Facets = customElements.get('facet-filters');
    if (!Facets || !Facets.render || !$('[data-collection-section]')) { location.href = u.pathname + u.search; return; }
    syncBudget(u, clearing);
    Facets.render(u.pathname + u.search);
  });

  /* ---------- recently viewed ---------- */
  const RECENT_KEY = 'mantire_recent_v2';
  try { localStorage.removeItem('mantire_recent'); } catch {}
  const readRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; } };
  const writeRecent = (list) => { try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 24))); } catch {} document.dispatchEvent(new CustomEvent('recent:updated')); };
  const saveRecent = (item) => {
    const list = readRecent().filter((x) => x.id !== item.id);
    list.unshift({ ...item, t: Date.now() });
    writeRecent(list);
  };
  const recentData = $('[data-recent-product]');
  if (recentData) { try { saveRecent(JSON.parse(recentData.textContent)); } catch {} }
  const sizedImage = (url, w) => { if (!url) return ''; try { const u = new URL(url, location.origin); u.searchParams.set('width', w); return u.pathname.startsWith('/') && u.origin === location.origin ? u.pathname + u.search : u.toString(); } catch { return url; } };
  const rtf = 'Intl' in window && Intl.RelativeTimeFormat ? new Intl.RelativeTimeFormat(document.documentElement.lang || 'en', { numeric: 'auto' }) : null;
  const timeAgo = (t, justNow) => {
    const s = Math.round((Date.now() - (t || Date.now())) / 1000);
    if (s < 60 || !rtf) return justNow || 'Just now';
    const steps = [[60, 'minute', 60], [3600, 'hour', 24], [86400, 'day', 7], [604800, 'week', 4.35], [2629800, 'month', 12], [31557600, 'year', Infinity]];
    for (const [sec, unit, max] of steps) { const v = Math.floor(s / sec); if (v < max) return rtf.format(-v, unit); }
    return '';
  };
  const syncRecentDots = () => { const n = readRecent().length; $$('[data-recent-dot]').forEach((d) => { d.hidden = n === 0; }); };
  syncRecentDots();
  document.addEventListener('recent:updated', syncRecentDots);

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      this.render();
      this.querySelector('[data-clear]').addEventListener('click', () => { writeRecent([]); this.hidden = true; });
      document.addEventListener('recent:updated', () => this.render());
    }
    render() {
      const exclude = Number(this.dataset.exclude || 0);
      // On Luxury pages the row lists luxury-priced fabrics only (the Recent drawer keeps the full history).
      // Luxury look: luxury-priced fabrics only; Fabric Mall look: fabrics up to the Mall cap (the Recent drawer keeps the full history).
      const lux = document.documentElement.dataset.mode === 'luxury';
      const fits = (x) => { const pr = Number(x.from ?? x.price); return !theme.luxuryFrom || !theme.mallMax || (lux ? pr >= theme.luxuryFrom : pr <= theme.mallMax); };
      const items = readRecent().filter((x) => x.id !== exclude && x.available !== false && fits(x)).slice(0, Number(this.dataset.limit) || 10);
      if (!items.length) { this.hidden = true; return; }
      const list = this.querySelector('[data-recent-list]');
      const tpl = this.querySelector('[data-recent-template]');
      list.innerHTML = '';
      items.forEach((x) => {
        const node = tpl.content.cloneNode(true);
        node.querySelectorAll('[data-href]').forEach((a) => (a.href = x.url));
        const img = node.querySelector('[data-img]');
        if (x.image) { img.src = sizedImage(x.image, Number(this.dataset.imgWidth) || 600); img.alt = x.title; } else img.remove();
        node.querySelector('[data-vendor]').textContent = x.vendor || '';
        node.querySelector('[data-title]').textContent = x.title;
        node.querySelector('[data-price]').textContent = formatMoney(x.price);
        const cmp = node.querySelector('[data-compare]');
        if (x.compare && x.compare > x.price) cmp.textContent = formatMoney(x.compare); else cmp.remove();
        list.appendChild(node);
      });
      this.hidden = false;
      const c = this.querySelector('carousel-slider'); c && c.update && c.update();
      const r = this.querySelector('scroll-row'); r && r.update && r.update();
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  class RecentDrawer extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-recent-items]');
      this.tpl = this.querySelector('[data-recent-row]');
      this.empty = this.querySelector('[data-recent-empty]');
      this.tools = this.querySelector('[data-recent-tools]');
      this.drawer = this.closest('drawer-panel');
      this.render();
      document.addEventListener('recent:updated', () => { if (!this._mutating) this.render(); });
      if (this.drawer) this.drawer.addEventListener('drawer:open', () => this.render(true));
      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-remove]');
        if (rm) {
          const li = rm.closest('.recent-item'); const id = Number(li.dataset.id);
          li.classList.add('is-removing');
          setTimeout(() => { this._mutating = true; writeRecent(readRecent().filter((x) => x.id !== id)); this._mutating = false; this.render(); }, 300);
          return;
        }
        if (e.target.closest('[data-recent-clear]')) {
          $$('.recent-item', this.list).forEach((li, i) => { li.style.animationDelay = `${i * 30}ms`; li.classList.add('is-removing'); });
          setTimeout(() => { writeRecent([]); this.render(); }, 360);
        }
      });
    }
    render(animate) {
      const items = readRecent().filter((x) => x.available !== false).slice(0, Number(this.dataset.limit) || 12);
      const count = $('[data-recent-count]', this.drawer || document);
      if (count) count.textContent = items.length ? `(${items.length})` : '';
      this.list.innerHTML = '';
      this.empty.hidden = items.length > 0;
      this.tools.hidden = items.length === 0;
      const total = $('[data-recent-total]', this);
      if (total) total.textContent = (items.length === 1 ? total.dataset.one : total.dataset.other).replace('[n]', items.length);
      items.forEach((x, i) => {
        const node = this.tpl.content.cloneNode(true);
        const li = node.querySelector('.recent-item');
        li.dataset.id = x.id;
        li.style.setProperty('--i', animate ? i : 0);
        if (!animate) li.style.animation = 'none';
        node.querySelectorAll('[data-href]').forEach((a) => (a.href = x.url));
        const img = node.querySelector('[data-img]');
        if (x.image) { img.src = sizedImage(x.image, 240); } else img.remove();
        node.querySelector('[data-vendor]').textContent = x.vendor || '';
        node.querySelector('[data-title]').textContent = x.title;
        node.querySelector('[data-price]').textContent = formatMoney(x.price);
        const cmp = node.querySelector('[data-compare]'); const save = node.querySelector('[data-save]');
        if (x.compare && x.compare > x.price) { cmp.textContent = formatMoney(x.compare); save.textContent = `−${Math.floor(((x.compare - x.price) * 100) / x.compare)}%`; } else { cmp.remove(); save.remove(); }
        node.querySelector('[data-time]').textContent = timeAgo(x.t, this.dataset.justNow);
        const form = node.querySelector('[data-recent-add]'); const choose = node.querySelector('[data-choose]');
        if (x.variant && !x.multi) { form.querySelector('[data-variant]').value = x.variant; choose.remove(); } else form.remove();
        const rm = node.querySelector('[data-remove]');
        rm.setAttribute('aria-label', (this.dataset.removeLabel || 'Remove [title]').replace('[title]', x.title));
        this.list.appendChild(node);
      });
    }
  }
  customElements.define('recent-drawer', RecentDrawer);

  /* ---------- read more ---------- */
  class ReadMore extends HTMLElement {
    connectedCallback() {
      const content = this.querySelector('[data-content]');
      const btn = this.querySelector('[data-toggle]');
      if (!content || !btn) return;
      requestAnimationFrame(() => { if (content.scrollHeight <= content.clientHeight + 24) { this.classList.add('is-expanded'); btn.hidden = true; } });
      btn.addEventListener('click', () => {
        const open = this.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', open);
        btn.textContent = open ? btn.dataset.less : btn.dataset.more;
        if (!open) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }
  customElements.define('read-more', ReadMore);

  /* ---------- Luxe / Mall switch (tab bar) with a curtain between pages ---------- */
  const buildCurtain = (mode, title, sub, state) => {
    const el = document.createElement('div');
    el.className = `mode-curtain mode-curtain--${mode} ${state || ''}`;
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = '<div class="mode-curtain__inner"><svg class="mode-curtain__mark" viewBox="0 -12 612 555"><use href="#mt-mark"/></svg><span class="mode-curtain__title"></span><span class="mode-curtain__line"></span><span class="mode-curtain__sub"></span></div>';
    el.querySelector('.mode-curtain__title').textContent = title || '';
    el.querySelector('.mode-curtain__sub').textContent = sub || '';
    document.body.appendChild(el);
    return el;
  };
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-mode-toggle]');
    if (!a) return;
    e.preventDefault();
    const to = (document.documentElement.dataset.mode === 'luxury') ? 'mall' : 'luxury';
    const url = to === 'luxury' ? a.dataset.luxuryUrl : a.dataset.mallUrl;
    try { localStorage.setItem('mantire_mode', to); } catch {}
    if (reduceMotion) { location.href = url; return; }
    const title = to === 'luxury' ? a.dataset.luxuryTitle : a.dataset.mallTitle;
    const sub = to === 'luxury' ? a.dataset.luxurySub : a.dataset.mallSub;
    try { sessionStorage.setItem('mantire_curtain', to); sessionStorage.setItem('mantire_curtain_text', JSON.stringify([title, sub])); } catch {}
    const el = buildCurtain(to, title, sub);
    void el.offsetWidth;
    el.classList.add('is-in');
    setTimeout(() => { location.href = url; }, 680);
  });
  (() => {
    const d = document.documentElement;
    if (!d.classList.contains('curtain-in')) return;
    let text = ['', ''];
    try { text = JSON.parse(sessionStorage.getItem('mantire_curtain_text') || '["",""]'); sessionStorage.removeItem('mantire_curtain'); sessionStorage.removeItem('mantire_curtain_text'); } catch {}
    const mode = d.dataset.curtain === 'mall' ? 'mall' : 'luxury';
    const el = buildCurtain(mode, text[0], text[1], 'is-settled');
    d.classList.remove('curtain-in');
    window.scrollTo(0, 0);
    const lift = () => {
      el.classList.remove('is-settled'); void el.offsetWidth; el.classList.add('is-out');
      const gone = () => el.remove();
      el.addEventListener('transitionend', (ev) => { if (ev.target === el) gone(); });
      setTimeout(gone, 1200);
    };
    setTimeout(lift, 320);
  })();
  // a page restored from the back/forward cache must not keep a curtain on screen
  window.addEventListener('pageshow', (e) => { if (e.persisted) { $$('.mode-curtain').forEach((c) => c.remove()); document.documentElement.classList.remove('curtain-in'); } });

  /* ---------- mode switch ---------- */
  const syncMode = () => {
    const mode = document.documentElement.dataset.mode || 'mall';
    $$('[data-mode-link]').forEach((a) => a.setAttribute('aria-current', a.dataset.modeLink === mode ? 'true' : 'false'));
    // In Luxury the logo leads to the Luxury home (the Premium page); the homepage itself is always the Fabric Mall.
    $$('a.header-logo').forEach((a) => {
      if (!a.dataset.homeHref) a.dataset.homeHref = a.getAttribute('href');
      a.setAttribute('href', mode === 'luxury' && theme.luxuryUrl ? theme.luxuryUrl : a.dataset.homeHref);
    });
  };
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-mode-link]');
    if (!a) return;
    try { localStorage.setItem('mantire_mode', a.dataset.modeLink); } catch {}
    document.documentElement.dataset.mode = a.dataset.modeLink;
    syncMode();
  });
  syncMode();

  /* ---------- count-up for quantities (years such as 1985 stay as they are) ---------- */
  const countUp = (el) => {
    const raw = el.textContent.trim();
    const m = raw.match(/^([^\d]*)([\d,]+)(.*)$/);
    if (!m || reduceMotion) return;
    const target = Number(m[2].replace(/,/g, ''));
    if (!target || (!m[2].includes(',') && !m[3] && target >= 1900 && target <= 2100)) return;
    const grouped = m[2].includes(',');
    const fmt = (n) => (grouped ? n.toLocaleString('en-US') : String(n));
    const start = performance.now(); const dur = 1600;
    el.style.minWidth = `${el.getBoundingClientRect().width}px`;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const e = 1 - Math.pow(2, -10 * t);
      el.textContent = `${m[1]}${fmt(Math.round(target * (t === 1 ? 1 : e)))}${m[3]}`;
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };
  if ('IntersectionObserver' in window) {
    const cio = new IntersectionObserver((entries) => entries.forEach((en) => { if (en.isIntersecting) { cio.unobserve(en.target); countUp(en.target); } }), { threshold: 0.6 });
    $$('[data-count-up]').forEach((el) => cio.observe(el));
  }

  /* ---------- WhatsApp: say which page the shopper is on ---------- */
  // Links to the store's WhatsApp (wa.me/<number>) get one more line before they open: the page's address, without the
  // tracking parameters Shopify adds to links. The shopper can still edit the message in WhatsApp. Share links (wa.me/?text=) are left as they are.
  const pageLink = () => {
    const u = new URL(location.href); const keep = new URLSearchParams();
    u.searchParams.forEach((v, k) => { if (!/^(_|utm_|pb$|preview_theme_id$|section_id$)/.test(k) && !keep.has(k)) keep.set(k, v); });
    const q = keep.toString();
    return `${u.origin}${u.pathname}${q ? `?${q}` : ''}`;
  };
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('a[href*="wa.me/"], a[href*="api.whatsapp.com/send"]');
    if (!a) return;
    let u; try { u = new URL(a.href); } catch (err) { return; }
    const number = u.hostname === 'wa.me' ? u.pathname.replace(/\D/g, '') : u.searchParams.get('phone');
    if (!number) return;
    const link = pageLink();
    const text = u.searchParams.get('text') || '';
    if (text.includes(link) || (location.pathname.length > 1 && text.includes(location.pathname))) return;
    u.searchParams.set('text', `${text ? `${text}\n\n` : ''}${theme.strings.waPage || 'I am currently on page:'} ${link}`);
    a.href = u.toString();
  }, true);

  /* ---------- card hover photos ---------- */
  // product-card renders the hover photo without a source; it gets one only where there is hover (a mouse): after the page has
  // loaded (still lazy, so only near the screen), for cards added later, and straight away for a card the pointer reaches.
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    const hoverPhoto = (img) => { if (img.dataset.hoverSrcset) { img.srcset = img.dataset.hoverSrcset; delete img.dataset.hoverSrcset; } };
    const allHover = debounce(() => $$('img[data-hover-srcset]').forEach(hoverPhoto), 250);
    document.addEventListener('pointerover', (e) => {
      const card = e.target.closest && e.target.closest('[data-product-card]');
      const img = card && card.querySelector('img[data-hover-srcset]');
      if (img) hoverPhoto(img);
    }, { passive: true });
    const startHover = () => { allHover(); new MutationObserver(allHover).observe(document.body, { childList: true, subtree: true }); };
    if (document.readyState === 'complete') startHover(); else window.addEventListener('load', startHover);
  }

  /* ---------- reveal on scroll ---------- */
  const REVEALS = '.reveal, .reveal-img, .gauge, [data-reveal]';
  if ('IntersectionObserver' in window && document.body.classList.contains('reveal-on')) {
    const io = new IntersectionObserver((entries) => entries.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add('is-revealed'); io.unobserve(en.target); }
    }), { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    $$(REVEALS).forEach((el) => io.observe(el));
  } else {
    $$(REVEALS).forEach((el) => el.classList.add('is-revealed'));
  }
  // Theme editor: a re-rendered section is shown straight away rather than waiting for a scroll that already happened.
  document.addEventListener('shopify:section:load', (e) => $$(REVEALS, e.target).forEach((el) => el.classList.add('is-revealed')));

  window.ManTire = { formatMoney, toast, Cart, saveRecent, readRecent, BagPop };
})();
